import { z } from 'zod';
import { objectId, timestampFields, FieldMetadata } from './base';

/**
 * TeamEvent Schema
 * Manager/admin-created non-game team activity (practice, scrimmage, social)
 * shown in the mobile schedule alongside games, with RSVP. Distinct from the
 * legacy `Event` model (route /events) — this one follows the games date
 * contract: `date` is the instant, `globalDate` the "M-DD-YYYY" display date
 * (derived server-side via gameDateTime.toGlobalDate), `time` a display
 * string.
 */
export const TeamEventSchema = z.object({
  _id: objectId,
  divisionTeam: objectId,
  organization: objectId.optional(),
  title: z.string().min(1).max(120),
  eventType: z.enum(['practice', 'scrimmage', 'social', 'other']).optional(),
  date: z.coerce.date(),
  globalDate: z.string().optional(),
  time: z.string().optional(),
  durationMinutes: z.number().int().positive().optional(),
  location: z.string().max(200).optional(),
  notes: z.string().max(1000).optional(),
  createdBy: objectId.optional(),
  isCancelled: z.boolean().optional(),
  ...timestampFields,
});

export type TeamEvent = z.infer<typeof TeamEventSchema>;

export const teamEventMetadata: Record<string, FieldMetadata> = {
  _id: { hidden: true },
  divisionTeam: { ref: 'DivisionTeam', label: 'Team' },
  organization: { ref: 'Organization', label: 'Organization' },
  title: { label: 'Title' },
  eventType: { label: 'Type' },
  date: { label: 'Date' },
  globalDate: { label: 'Display Date' },
  time: { label: 'Time' },
  durationMinutes: { label: 'Duration (min)' },
  location: { label: 'Location' },
  notes: { label: 'Notes' },
  createdBy: { ref: 'User', label: 'Created By' },
  isCancelled: { label: 'Cancelled' },
  created: { hidden: true },
  updated: { hidden: true },
};
